import { Injectable, Inject, PLATFORM_ID, TransferState, makeStateKey, StateKey, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

import { ApiService } from './api.service';
import { ThemeService } from "./theme.service";
import { MenuDTO } from 'src/app/shared/model/dto/MenuDTO';
import { DebugModeService } from './debugMode.service';
import { LoadingSpinnerService } from 'src/app/core/services/loadingSpinner.service';
import { HoursTemplate } from 'src/app/shared/model/HoursTemplate';
import { TranslateService } from '@ngx-translate/core';
import { CoupomStorageService } from './coupomLocalStorage.service';

import { Cardapnator } from 'cardapnator';
import { CartRecoverService } from './cartRecover.service';
import RestaurantOperations from  'restaurantoperations';
import CardapioCache from  'cardapiocache';
import { TableService } from './table.service';
import { DiscordLogService } from './discord-log.service';
import { CARDAPIO_DO_RENDER, cardapioParaORender, envelopeParaOEstado } from './ssr/cardapio-do-render';
import { fusoDaLoja } from './ssr/validade-da-pagina';
import { zerarConfiguracaoGlobalDaCardapnator } from './cardapnator-sem-heranca';
import { AutoCompletePhoneNumberService } from './autoCompletePhoneNumber.service';

import type MiniSearch from 'minisearch';
import { LocalStorageService } from './localStorage.service';
import { OpenPanelService } from './openPanel.service';
import { aposPrimeiroGesto } from '../../shared/utils/terceiros-fora-do-boot';
import { idiomasParaTentar } from './idiomas-disponiveis';
import { captureExternalSource } from './external-source';

export const DEFAULT_LANGUAGE = 'pt-br';

@Injectable()
export class RestaurantService {
    public restaurant: any;
    loaded: boolean = false;

    /**
     * Por que o cardápio não veio, quando não veio.
     *
     * `'prazo'` significa que o carregamento estourou o prazo do boot
     * (`PRAZO_CARDAPIO_MS`), NÃO que a loja não existe. A distinção existe
     * porque `restaurant` fica `undefined` nos dois casos, e a tela precisa
     * dizer coisas opostas: "loja não encontrada" manda o cliente embora de vez,
     * enquanto "não conseguimos carregar, tente de novo" é o que serve a quem
     * está numa rede ruim. Ver `menu.page.ts` e `core.module.ts`.
     */
    falhaDeCarga: 'prazo' | null = null;
    /**
     * Preenchido quando o cardápio veio do HTML pré-renderizado (TransferState), com o slug e o
     * hash que o servidor usou. É o que o `FrescorDoHtmlService` compara com o hash atual depois
     * da hidratação. `null` em todo boot sem SSR.
     */
    htmlDoServidor: { url: string; hash: string } | null = null;

    /** No servidor, o cardápio que a Lambda de render já baixou (ver `cardapio-do-render.ts`). */
    private readonly cardapioDoRender = inject(CARDAPIO_DO_RENDER, { optional: true });
    public menuType: 'table' | 'delivery' = 'delivery';

    public externalSourceDetails: {
        src: string,
        campaign: string,
        uuid: string
    }

    paymentMethodOperationInfo: any = {
        operatedValue: '',
        paymentMethodOperation: '',
        paymentMethodValueType: '',
        paymentMethodValue: '', 
    }

    private extraCategoriesMinPrice = [];

    // Cache do índice de busca (MiniSearch): construir o índice varre todo o menu e é
    // caro; reconstruí-lo a cada tecla digitada travava a busca em cardápios grandes.
    // Guardamos o índice por referência de menu — invalida sozinho quando o menu troca
    // (ex.: reloadRestaurant devolve um objeto novo).
    private searchIndexMenuRef: any = null;
    private searchIndex: MiniSearch | null = null;

    private static readonly CARDAPIO_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

    private static readonly NEW_LAYOUT_PREVIEW_PARAM = 'pd_preview';

    constructor(
        private apiService: ApiService,
        private themeService: ThemeService,
        private debugModeService: DebugModeService,
        private LoadingSpinnerService: LoadingSpinnerService,
        private translateService: TranslateService,
        private cartRecoverService: CartRecoverService,
        private tableService: TableService,
		private localStorageService: LocalStorageService,
		private autoCompletePhoneNumberService: AutoCompletePhoneNumberService,
		private openPanelService: OpenPanelService,
		@Inject(PLATFORM_ID) private platformId: Object,
		private transferState: TransferState,
    ) {}

    private cardapioStateKey(url: string): StateKey<any> {
        return makeStateKey<any>(`cardapio:${url}`);
    }

    /**
     * Aquisição do cardápio consciente de plataforma (SSR/prerender):
     * - server: fetch direto do cardapio.json (envelope {hash, cardapio}) via fetch
     *   global do Node e grava o envelope na TransferState para o cliente reusar.
     * - browser: consome primeiro a TransferState semeada pelo server (e semeia o
     *   localStorage cardapio.cache/hash) para NÃO re-baixar os ~958KB; se ausente,
     *   segue o fluxo atual (preload inline → CardapioCache).
     * Retorna o objeto `cardapio` (info/menu/hours) ou null.
     */
    private acquireCardapio(url: string): Promise<any> {
        const key = this.cardapioStateKey(url);

        if (!isPlatformBrowser(this.platformId)) {
            const doRender = this.cardapioDoRender;
            if (doRender && doRender.slug === url && doRender.envelope && doRender.envelope.cardapio) {
                // O estado leva o cardápio CRU; o render monta sobre uma cópia (ver cardapioParaORender).
                this.transferState.set(key, envelopeParaOEstado(doRender.envelope));
                return Promise.resolve(cardapioParaORender(doRender.envelope.cardapio));
            }
            return fetch(`https://cardapio.multipedidos.com.br/${url}/cardapio.json`, {
                headers: { 'Cache-Control': 'no-cache' }
            })
                .then((r) => (r.status === 200 ? r.json() : null))
                .then((envelope: any) => {
                    if (!envelope || !envelope.cardapio) return null;
                    // Embute o cardápio na TransferState p/ o cliente NÃO re-buscar 958KB:
                    // no gate, com o embedding o LCP caiu −43% (sem ele o cliente re-busca
                    // e bloqueia o boot → LCP volta a ~14s). O custo é HTML maior (FCP),
                    // mitigado pelo render bounded (só o 1º lote no DOM). Ver memória do gate.
                    this.transferState.set(key, envelopeParaOEstado(envelope));
                    return cardapioParaORender(envelope.cardapio);
                })
                .catch(() => null);
        }

        if (this.transferState.hasKey(key)) {
            const envelope = this.transferState.get<any>(key, null);
            this.transferState.remove(key);
            if (envelope && envelope.cardapio) {
                if (envelope.hash) this.htmlDoServidor = { url, hash: envelope.hash };
                try {
                    this.localStorageService.setItem(`cardapio.cache.${url}`, JSON.stringify(envelope.cardapio));
                    this.localStorageService.setItem(`cardapio.hash.${url}`, envelope.hash);
                } catch (e) { /* quota excedida: segue com o dado em memória */ }
                return Promise.resolve(envelope.cardapio);
            }
        }

        return this.consumeMenuPreload(url).then((preloaded) => {
            if (preloaded) return preloaded;
            return (new CardapioCache(url)).get();
        });
    }

    // ⚠️⚠️ TEMPORÁRIO — MOCK LOCAL PARA TESTES ⚠️⚠️
    // Quando o slug for 'viaroma', carrega o cardápio de src/assets/mock/viaroma.json
    // (baixado de api.multipedidos.com.br/restaurant/data/v2/viaroma) em vez de bater no
    // CDN — permite testar o app localmente sem a rede da Multipedidos. Mesmo shape
    // {info, menu, hours} que o /restaurant/data/v2/. REMOVER quando não precisar mais.
    //
    // `!environment.production` é obrigatório: sem ele o desvio vale também nos builds
    // publicados, e o `viaroma` de blue/green/prod passa a servir este cardápio
    // CONGELADO. O sintoma não parece um mock — parece o tema quebrado, porque o
    // theme embutido aqui é o vermelho default (id 9863, #EF4342) enquanto o
    // restaurante real usa preto (id 507, #000000); o ThemeService aplica fielmente
    // o que recebe. Aconteceu no blue em 2026-08-04.
    private acquireCardapioOrViaromaMock(url: string): Promise<any> {
        if (!environment.production && isPlatformBrowser(this.platformId) && String(url).toLowerCase() === 'viaroma') {
            const mockUrl = new URL('assets/mock/viaroma.json', document.baseURI).href;
            return fetch(mockUrl)
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null);
        }
        return this.acquireCardapio(url);
    }

    loadByURL(url): Promise<MenuDTO> {
        console.log('subiu 1.6');

        return new Promise(async (resolve, reject) => {
            const isBrowser = isPlatformBrowser(this.platformId);
            let params: URLSearchParams | null = null;

            if (isBrowser) {
                params = new URLSearchParams(window.location.search);
                this.externalSourceDetails = {
                    src: captureExternalSource(window.location.search, url, this.sessionStorageOuNull()),
                    campaign: params.get('campaign'),
                    uuid: params.get('u')
                }

                await this.cartRecoverService.checkUuid(this.externalSourceDetails.uuid);

                this.invalidateStaleCardapioCache(url);
            }

            const cardapioHashBefore = isBrowser ? this.localStorageService.getItem(`cardapio.hash.${url}`) : null;

            this.acquireCardapioOrViaromaMock(url).then((data) => {
                if(!data){
                    return isBrowser ? this.apiService.get(`/restaurant/data/v2/${url}`).toPromise() : null;
                }

                return data;
            }).catch((e) => {
                return isBrowser ? this.apiService.get(`/restaurant/data/v2/${url}`).toPromise() : null;
            }).then(async (data: any) => {
                const cardapioHashAfter = this.localStorageService.getItem(`cardapio.hash.${url}`);
                if (cardapioHashAfter && cardapioHashAfter !== cardapioHashBefore) {
                    this.localStorageService.setItem(`cardapio.timestamp.${url}`, Date.now().toString());
                }

                if(!data){
                    this.loaded = true;
                    this.LoadingSpinnerService.dismiss();
                    resolve(null);
                    return;
                }

                // Aberto/fechado no relógio da LOJA, não no da máquina (restaurantoperations 1.1.0). O
                // servidor do SSR roda em UTC: sem o fuso, `info.is_closed` saía "fechada" nas últimas
                // 3 h do turno de loja em Brasília (OP-1233). No navegador em outro fuso, o mesmo erro.
                data = (new RestaurantOperations(data)).setTimeZone(fusoDaLoja(data.info)).build();

                // validateTableLink lê localStorage/window.location; no server pula
                // (delivery é o default do prerender). O cliente revalida na hidratação.
                const isTableLink = isBrowser ? await this.tableService.setRestaurantInfo(data.info).validateTableLink() : false;
                // Item "só no almoço" no relógio da LOJA, não no da máquina: o servidor do SSR roda em
                // UTC, e um cliente pode estar em outro fuso. É o mesmo fuso que a validade da página
                // (`validade-da-pagina.ts`) usa para dizer até quando o HTML vale.
                // Sem herança da loja anterior: a lib guarda estoque e combo em globais (ver o arquivo).
                zerarConfiguracaoGlobalDaCardapnator();
                const cardapnator = new Cardapnator(data).setTimeZone(fusoDaLoja(data.info));

                if (isTableLink) {
                    this.menuType = 'table';
                    cardapnator.onlyTableProducts();
                }

                if (this.menuType === 'delivery')
                    cardapnator.onlyDeliveryProducts();

                data = (new RestaurantOperations(data)).setTimeZone(fusoDaLoja(data.info)).build();

                this.restaurant = cardapnator.setMenuType(this.menuType).build();

                this.applyNewLayoutPreviewMode(params);

                const isGreen = environment.apiURL.includes('green');

                // Grupo de fluxo da sessão pro funil poder ser filtrado por tipo (mesa e app
                // são jornadas estruturalmente diferentes — ex.: mesa não passa pelas mesmas
                // etapas do app). Os canais `totem`/`tablet` saíram junto com o kiosk.
                const flowType = this.menuType === 'table' ? 'table' : 'app';

                const openPanelGreenAllowList = [1, 460];
                if (!isGreen || openPanelGreenAllowList.includes(this.restaurant.info.id)) {
                    this.openPanelService.init(this.restaurant.info.id, flowType, !this.restaurant.info.is_closed);
                }

                const excludedFromAnalytics = [1, 460];
                const greenAllowList = [13030];
                const shouldLoadAnalytics = isBrowser && (!isGreen || greenAllowList.includes(this.restaurant.info.id)) && !excludedFromAnalytics.includes(this.restaurant.info.id);

                if(shouldLoadAnalytics) {
                    const isNewHome = !!this.restaurant.info.modules?.new_home_pedir_delivery;
                    const homeVersion = isNewHome ? 'novo' : 'legado';
                    this.initializeGoogleAnalytics('G-FMS7MD9BDR', { user_properties: { home_version: homeVersion } });
                    this.initializeGoogleAnalytics('UA-123254716-2');
                }
                if(shouldLoadAnalytics && this.restaurant.info.google_tag_manager_id) this.initializeGoogleTagManager(this.restaurant.info.google_tag_manager_id)
                if(shouldLoadAnalytics && this.restaurant.info.google_analytics_id) this.initializeGoogleAnalytics(this.restaurant.info.google_analytics_id)
                if (this.restaurant.menu.combos.length > 0) this.checkCombosAvailability(this.restaurant.menu.combos);
                if (this.restaurant.menu.pizzas.sizes.length > 0) this.checkPizzaAvailability(this.restaurant.menu.pizzas.sizes);
                this.parseProductsShowFrom(this.restaurant);
                this.getFeaturedItems(this.restaurant.menu);
                if(!this.debugModeService.getMode()) this.setRestaurantTheme();

                const userPhoneUUID = (isBrowser && params) ? params.get('wpu') : null;
				if (isBrowser && params && userPhoneUUID) {
					params.delete('wpu');
					const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
					window.history.replaceState({}, '', newUrl);

					const phoneNumber = await this.autoCompletePhoneNumberService.get_phone_number(userPhoneUUID);
					if (phoneNumber) {
						console.log('Auto-completed phone number:', phoneNumber.phoneNumber);
	                    this.localStorageService.setItem(`phone-${this.restaurant.info.id}`, phoneNumber.phoneNumber);
					} else {
						console.log('Failed to auto-complete phone number or it has expired.');
					}
                }

                this.loaded = true;
                this.LoadingSpinnerService.dismiss();
                resolve(data);
            }).catch((error: any) => {
                if(error.error == 'Restaurant not found'){
                    this.loaded = true;
                    this.LoadingSpinnerService.dismiss();
                    resolve(null);
                    return;
                }

                reject(error);
            }).finally(() => {
                this.setTranslations(this.restaurant);
                this.LoadingSpinnerService.dismiss();
            })
        });
    }

    /**
     * Consome o preload do cardápio disparado pelo index.html (Caso 16): o
     * inline script inicia o fetch (CDN → fallback API) em paralelo com o boot
     * do JS; aqui a promise é consumida uma única vez. No hit de CDN replica a
     * gravação de cache/hash que o CardapioCache faria. Qualquer falha → null
     * e o fluxo normal (CardapioCache) segue intocado.
     */
    private async consumeMenuPreload(url: string): Promise<any | null> {
        const preload = (window as any).__mpMenuPreload;
        if (!preload || preload.slug !== url || !preload.promise) return null;
        (window as any).__mpMenuPreload = null;
        try {
            const hit = await preload.promise;
            if (!hit || !hit.payload) return null;
            if (hit.source === 'cdn') {
                const cardapio = hit.payload.cardapio;
                if (!cardapio) return null;
                try {
                    this.localStorageService.setItem(`cardapio.cache.${url}`, JSON.stringify(cardapio));
                    this.localStorageService.setItem(`cardapio.hash.${url}`, hit.payload.hash);
                } catch (e) { /* quota excedida: segue com o dado em memória */ }
                return cardapio;
            }
            return hit.payload;
        } catch (e) {
            return null;
        }
    }

    /** Safari privado e iframe sem storage lançam só de LER `window.sessionStorage`. */
    private sessionStorageOuNull(): Storage | null {
        try {
            return window.sessionStorage;
        } catch (e) {
            return null;
        }
    }

    private applyNewLayoutPreviewMode(params: URLSearchParams | null): void {
        // No servidor não há query string (`params` é null): sem esta guarda o `.get` estourava
        // DENTRO do `loadByURL`, o `catch` engolia o erro e a montagem parava no meio — os
        // destaques (`getFeaturedItems`) nunca eram criados e o prerender quebrava lá no menu.
        if (!params) return;
        const isNewLayoutPreview = params.get(RestaurantService.NEW_LAYOUT_PREVIEW_PARAM) === '1';
        if (!isNewLayoutPreview || !this.restaurant?.info) return;

        this.restaurant.info.modules = {
            ...this.restaurant.info.modules,
            new_home_pedir_delivery: 1,
            new_complements_pedir_delivery: 1,
            new_flow_checkout_pedir_delivery: 1,
        };
    }

    private invalidateStaleCardapioCache(url: string): void {
        const timestamp = this.localStorageService.getItem(`cardapio.timestamp.${url}`);
        const hasCache = !!this.localStorageService.getItem(`cardapio.cache.${url}`);

        // hasCache sem timestamp = cache anterior ao deploy do TTL; força refresh único
        const isStale = (hasCache && !timestamp) ||
            (timestamp && (Date.now() - parseInt(timestamp, 10)) > RestaurantService.CARDAPIO_CACHE_MAX_AGE_MS);

        if (!isStale) return;

        this.localStorageService.removeItem(`cardapio.cache.${url}`);
        this.localStorageService.removeItem(`cardapio.hash.${url}`);
        this.localStorageService.removeItem(`cardapio.timestamp.${url}`);
    }

    setTranslations(restaurantData) {
        let restaurant_info_language = restaurantData?.info.language == 'ptBR' ? 'pt-br' : restaurantData?.info.language;
        const browserLanguage = (typeof navigator !== 'undefined') ? navigator.language : null;
        // Só idiomas que o build PUBLICA. Antes a lista ia crua para o `use()` e
        // a descoberta de "não existe" dependia da falha — que nunca vinha como
        // 404: o SPA fallback devolve o `index.html` em HTTP 200, e o erro só
        // aparecia no `JSON.parse`, depois de baixar 34 KB de HTML à toa e de
        // gerar um evento de erro (Sentry PEDIRDELIVERY-3, navegadores `es-419`).
        // A cadeia de fallback continua valendo — para falha de REDE, que é o
        // que ela sempre deveria ter tratado.
        const languages = idiomasParaTentar([browserLanguage, restaurant_info_language, DEFAULT_LANGUAGE]);

        const applyTranslation = () => {
            const language = languages.shift();
            // Sem idioma restante (ex.: no server o loader HTTP de i18n falha e esgota
            // a lista) — para a recursão em vez de estourar em undefined.toLowerCase().
            if (!language) return;
            this.translateService.use(language.toLowerCase()).subscribe(() => {}, () => applyTranslation());
        }
        applyTranslation();
    }

    checkCombosAvailability(menuCombos) {
        menuCombos = menuCombos.map(combo => {
            if (combo.pizzaSizes.length == 0) combo.unavailable = true;
            return combo;
        })
    }

    checkPizzaAvailability(menuPizzaSizes) {
        menuPizzaSizes = menuPizzaSizes.map(pizzaSize => {
            if (pizzaSize.flavors.length == 0) pizzaSize.unavailable = true;
            return pizzaSize;
        })
    }

    getPriceFromCombos(restaurantData){
        restaurantData.menu.combos = restaurantData.menu.combos.map(combo => {
            if(combo.price) {
                combo.fromPrice = combo.price; 
                return combo;
            }

            let minPrice = 0;
            combo.pizzaSizes.forEach(sizeID => {
                let size = restaurantData.menu.pizzas.sizes.find(foundSize => foundSize.id == sizeID);
                minPrice += this.calculatePizzaMinimalPrice(size, true);
            });

            let extraCategories = [];
            combo.extras.forEach(extraID => {
                let extra = combo.extras.find(foundExtra => foundExtra.id == extraID);
                if(extra) extraCategories.push(extra);
            });
            minPrice += this.calculateMinimalPriceFromExtraCategories(extraCategories);

            combo.fromPrice = minPrice;
            return combo;
        });
    }

    getPriceFromPizzas(restaurantData){
        restaurantData.menu.pizzas.sizes = restaurantData.menu.pizzas.sizes.map(pizzaSize => {
            if (!pizzaSize.unavailable) pizzaSize.fromPrice = this.calculatePizzaMinimalPrice(pizzaSize);
            return pizzaSize;
        })
    }

    getPriceFromGenericProduct(restaurantData) {
        restaurantData.menu.general = restaurantData.menu.general.map(item => {            
            if (!item.unavailable && item.products.length > 0) {
                item.products = item.products.map(product => {
                    if (!product.unavailable && product.price == 0) {
                        product.fromPrice = this.calculateFromPrice(product);
                    }
                    return product;
                });
            }
            return item;
        });
    }

    parseProductsShowFrom(restaurantData) {
        if(restaurantData.info.show_from){
            this.getPriceFromCombos(restaurantData);
            this.getPriceFromPizzas(restaurantData);
        }

        this.getPriceFromGenericProduct(restaurantData);        
    }

    calculatePizzaMinimalPrice(pizza, combo=false) {
        if((pizza.price === 0 && pizza.priceBehavior === 'incremental') || combo) {
            let prices = [];
            pizza.flavors.forEach(flavorCategory => {
                if('fractions' in flavorCategory) {
                    flavorCategory.fractions.forEach(fraction => {
                        if(fraction.price) prices.push(fraction.price);
                        fraction.exception.forEach(exception => {
                            if(exception.price) prices.push(exception.price);
                        });
                    });
                }
            });

            return prices.length == 0 ? 0 : Math.min.apply(Math, prices);
        }

        return pizza.price;
    }

	calculateFromPrice(item) {
        const extraCategories = [];

        item.extras.forEach(itemExtra => {
            const extra = this.restaurant.menu.extras.find(filteredItem => itemExtra == filteredItem.id);

            if (extra)
                extraCategories.push(extra);
        });

        const fromPrice = this.calculateMinimalPriceFromExtraCategories(extraCategories);
		return fromPrice;
	}

    // OP-1064: a obrigatoriedade de um grupo de complemento depende do TIPO dele.
    // Grupo "único" (type 1) -> quem manda é o campo `required`.
    // Grupo "múltiplo" (type 2/3) -> quem manda é `qty_min`; o `required` é ignorado.
    // Ler `required` para grupo múltiplo fazia complemento OPCIONAL entrar no "A partir de":
    // o NEP grava required=1 no grupo e não zera ao trocar o tipo para múltiplo, e esse campo
    // nem é exibido para o lojista nesse tipo (só aparece em "único"), então o dado sujo é
    // invisível. Mesma regra que o dashboard já usa em nepZeroPriceService.isCategoryMandatory.
    isMandatoryExtraCategory(category: any): boolean {
        const isUniqueExtra = category.type == 1;

        return isUniqueExtra ? !!category.required : category.qty_min > 0;
    }

    calculateMinimalPriceFromExtraCategories(extraCategories) {
        let currentPrice = 0;
        let hasRequiredCategory = extraCategories.filter(category => {
            return this.isMandatoryExtraCategory(category)
        }).length;

        extraCategories.forEach((category) => {
            if(this.isMandatoryExtraCategory(category) || !hasRequiredCategory) {
                const minPrice = this.getMinValueFromCategory(category);
                if(minPrice != 0) {
                    currentPrice += minPrice;
                }
            }
        });
		return currentPrice;
    }

    getMinValueFromCategory(category) {
        if (this.extraCategoriesMinPrice[category.id]) 
            return this.extraCategoriesMinPrice[category.id];

        const availableOptions = category.options.filter(option => option.available && !option.asTitle);
        
        if (availableOptions.length === 0) {
            this.extraCategoriesMinPrice[category.id] = 0;
            return 0;
        }

        const quantityMin = category.qty_min || 1;
        const { type, qtyPerItem } = category;
        const isUniqueExtra = type === 1;

        if (isUniqueExtra) {
            const minPrice = Math.min.apply(Math, availableOptions.map(option => option.price));
            this.extraCategoriesMinPrice[category.id] = minPrice;
            return minPrice;
        }

        const hasLimitPerItem = qtyPerItem && qtyPerItem > 0;

        if (hasLimitPerItem) {
            const sortedOptions = availableOptions
                .map(option => option.price)
                .sort((a, b) => a - b);
            
            let itemsNeeded = quantityMin;
            let minPrice = 0;

            for (const price of sortedOptions) {
                if (itemsNeeded === 0) break;
                
                const quantityToUse = Math.min(qtyPerItem, itemsNeeded);
                minPrice += price * quantityToUse;
                itemsNeeded -= quantityToUse;
            }

            this.extraCategoriesMinPrice[category.id] = minPrice;
            return minPrice;
        }

        const cheapestPrice = Math.min.apply(Math, availableOptions.map(option => option.price));
        const minPrice = cheapestPrice * quantityMin;
        this.extraCategoriesMinPrice[category.id] = minPrice;
        return minPrice;
    }

    setRestaurantTheme() {
        if (!this.restaurant.info.theme) return;

        this.themeService.setTheme(this.restaurant.info.theme);
    }

    getFeaturedItems(restaurantMenu) {
        let featuredItems = [];

        let menuItems = [...restaurantMenu.combos.filter(combo => !combo.unavailable), ...restaurantMenu.pizzas.sizes.filter(pizza => !pizza.unavailable)];

        let availableCategories = restaurantMenu.general.filter(general => !general.unavailable && !general.unavailableByTemplate);

        availableCategories.forEach(category => category.products.forEach(product => {
            if (product.unavailable) return;
            menuItems.push(product);
        }))        

        menuItems.forEach(menuItem => {
            if (!menuItem.featured) return;
            featuredItems.push(menuItem);
        })

        restaurantMenu['featuredItems'] = featuredItems;
    }

    private buildSearchIndex(Classe: typeof MiniSearch, menu): MiniSearch {
        const miniSearch = new Classe({
            fields: ['name', 'description'],
            storeFields: ['name', 'description'],
            idField: 'id',
            searchOptions: {
              fuzzy: 0.2,
              boost: { name: 2, description: 1 },
              prefix: true,
              combineWith: 'AND',
            },
            tokenize: (s: string) => {
              let normalized = s
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');

              normalized = normalized.replace(/[^a-z0-9\s]/g, ' ');
              normalized = normalized.replace(/\s+/g, ' ').trim();

              return normalized.split(' ').filter(term => term.length > 1);
            }
          });

        const itemsToIndex = [];
        const info = this.restaurant?.info;
        const showStockPaused = info?.modules?.ingredient_inventory
            ? info?.supply_stock_config?.display_paused_items_as_sold_out ?? false
            : info?.stock_configuration?.display_paused_items_as_sold_out ?? false;

        menu.general.forEach(category => {
            if (category.unavailable || category.unavailableByTemplate) return;
            category.products.forEach(product => {
                if (product.unavailable || product.unavailableByTemplate) return;
                if (product.totalCalcType === 'WEIGHT') return;
                if (product.hasStock === false && !showStockPaused) return;
                itemsToIndex.push({
                    id: `general-${category.name}-${product.name}`,
                    name: product.name,
                    description: product.description || '',
                });
            });
        });

        menu.pizzas.sizes.forEach(size => {
            if (size.unavailable || size.unavailableByTemplate) return;
            itemsToIndex.push({
                id: `pizzaSize-${size.name}`,
                name: size.name,
                description: size.description || '',
            });
        });

        menu.combos.forEach(combo => {
            if (combo.unavailable || combo.unavailableByTemplate) return;
            itemsToIndex.push({
                id: `combo-${combo.name}`,
                name: combo.name,
                description: combo.description || '',
            });
        });

        menu.featuredItems.forEach(item => {
            if (item.unavailable || item.unavailableByTemplate) return;
            if (item.totalCalcType === 'WEIGHT') return;
            if (item.hasStock === false && !showStockPaused) return;
            itemsToIndex.push({
                id: `featured-${item.name}`,
                name: item.name,
                description: item.description || '',
            });
        });

        miniSearch.addAll(itemsToIndex);
        return miniSearch;
    }

    /**
     * O `minisearch` (70 KB de fonte) só chega quando alguém BUSCA.
     *
     * Ele estava no bundle do boot por causa do import de topo, mas o índice já era construído
     * sob demanda — ou seja, toda visita baixava a biblioteca e a esmagadora maioria nunca
     * digitava nada no campo de busca. O import dinâmico mantém o índice cacheado por
     * referência de menu, como antes; o que mudou é quando o CÓDIGO da biblioteca chega.
     */
    private async getSearchIndex(menu): Promise<MiniSearch> {
        if (this.searchIndex && this.searchIndexMenuRef === menu) {
            return this.searchIndex;
        }
        const { default: Classe } = await import('minisearch');
        this.searchIndex = this.buildSearchIndex(Classe, menu);
        this.searchIndexMenuRef = menu;
        return this.searchIndex;
    }

    async filterMenu(str, menu) {
        if (!str.trim()) {
            return menu;
        }

        // Índice reaproveitado entre teclas — só reconstruído se o menu trocar de referência.
        const indice = await this.getSearchIndex(menu);
        const results = new Set(
            indice.search(str.toLowerCase()).map(result => result.id)
        );

        // Reconstrução estrutural rasa em vez de JSON.parse(JSON.stringify(menu)): recriamos
        // só os arrays/objetos que precisam encolher; produtos são reaproveitados por
        // referência (o menu original nunca é mutado). Evita clonar ~1MB por busca.
        const filteredMenu: any = { ...menu };

        filteredMenu.general = menu.general.reduce((acc, category) => {
            if (category.unavailable || category.unavailableByTemplate) return acc;
            const products = category.products.filter(product =>
                results.has(`general-${category.name}-${product.name}`)
            );
            if (products.length > 0) acc.push({ ...category, products });
            return acc;
        }, []);

        filteredMenu.pizzas = {
            ...menu.pizzas,
            sizes: menu.pizzas.sizes.filter(size => results.has(`pizzaSize-${size.name}`)),
        };

        filteredMenu.combos = menu.combos.filter(combo => results.has(`combo-${combo.name}`));

        filteredMenu.featuredItems = menu.featuredItems.filter(item =>
            results.has(`featured-${item.name}`)
        );

        return filteredMenu;
    }

    checkCoupon(code): Promise<any>{
        return this.apiService.post(`/restaurant/${this.restaurant.info.id}/coupom/check`, {code: code}).toPromise();
    }

    checkPixAsaasPayment(orderID): Promise<any> {
        return this.apiService.get(`/restaurant/${this.restaurant.info.id}/order/${orderID}/check-pix-asaas-payment`).toPromise();
    }

    checkPaymentMethodOperation(method, grandTotal){
        let operationActive = false
        this.paymentMethodOperationInfo.paymentMethodOperation = 'Addition';
        this.paymentMethodOperationInfo.paymentMethodValueType = 'fix';
        this.paymentMethodOperationInfo.paymentMethodValue = 0;
        this.restaurant.info.paymentSettings.forEach(setting => {
            if(method == setting.paymentType || method == setting.paymentType){
                if(setting.isActive && setting.value > 0){
                    this.paymentMethodOperationInfo.operatedValue = setting.value;
                    if(setting.operation == 'discount'){
                        this.paymentMethodOperationInfo.paymentMethodOperation = 'Discount';
                        if(setting.valueType == "percent"){
                            this.paymentMethodOperationInfo.paymentMethodValueType = 'percent';
                            this.paymentMethodOperationInfo.paymentMethodValue = -(grandTotal*setting.value/100);
                        }else{
                            this.paymentMethodOperationInfo.paymentMethodValue = -(setting.value);
                        }
                    }else{
                        if(setting.valueType == "percent"){
                            this.paymentMethodOperationInfo.paymentMethodValueType = 'percent';
                            this.paymentMethodOperationInfo.paymentMethodValue = grandTotal*setting.value/100;
                        }else{
                            this.paymentMethodOperationInfo.paymentMethodValue = setting.value;
                        }
                    }
                    operationActive = true;
                }
            }
        });
        return operationActive;
    }

    getScheduleHourTemplateByID(templateID: number): Promise<HoursTemplate> {
        return new Promise((resolve, reject) => {
            this.apiService.get(`/restaurant/${this.restaurant.info.id}/hours-template/${templateID}/schedule`).subscribe(response => {
                resolve(response);
            }, error => {
                reject(error);
            });
        });
    }

    updateCoordinates(address) {
        return new Promise((resolve) => {
            this.apiService.put(`/restaurant/${this.restaurant.info.id}/coordinates`, {address}).subscribe(response => {
                resolve(response);
            });
        })
    }

    /**
     * O `dataLayer` é uma FILA: o `gtm.start` pode ser empilhado antes de o SDK
     * existir, porque o gtm.js consome a fila ao carregar. Por isso o carimbo de
     * início continua IMEDIATO (preserva o horário real da visita) e só o download
     * sai do caminho crítico, no primeiro gesto do usuário.
     */
    initializeGoogleTagManager(googleTagManagerID){
        let googleTagManagerloadingTag = document.createElement("script");
        googleTagManagerloadingTag.innerText = `(function(w,l){w[l]=w[l]||[];w[l].push({\'gtm.start\':new Date().getTime(),event:\'gtm.js\'});})(window,\'dataLayer\');`
        document.body.appendChild(googleTagManagerloadingTag);

        aposPrimeiroGesto(() => {
            const gtmScript = document.createElement("script");
            gtmScript.async = true;
            gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=' + googleTagManagerID;
            document.body.appendChild(gtmScript);
        });
    }

    /**
     * Mesmo contrato do GTM: `gtag('js'|'config', ...)` vai para o `dataLayer` na
     * hora — nenhum pageview se perde nem chega com o horário errado — e o
     * `gtag/js` (168 kB por ID, e são até três IDs) só baixa no primeiro gesto.
     * Medido em prod (/viaroma, 06/08/2026): os quatro downloads de gtag entravam
     * entre 1.524ms e 1.555ms, em cima da construção do cardápio.
     */
    initializeGoogleAnalytics(googleAnalyticsID, extraConfig: object = {}){

        const configArgs = Object.keys(extraConfig).length ? `, ${JSON.stringify(extraConfig)}` : '';
        let googleAnalyticsInitializationTag = document.createElement("script");
        googleAnalyticsInitializationTag.innerText = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date()); gtag(\'config\',\'${googleAnalyticsID}\'${configArgs});`
        document.body.appendChild(googleAnalyticsInitializationTag);

        aposPrimeiroGesto(() => {
            const googleAnalyticsLoadingTag = document.createElement("script");
            googleAnalyticsLoadingTag.type = "application/javascript";
            googleAnalyticsLoadingTag.async = true;
            googleAnalyticsLoadingTag.src = "https://www.googletagmanager.com/gtag/js?id=" + googleAnalyticsID;
            document.body.appendChild(googleAnalyticsLoadingTag);
        });
    }
}
